import React, { createContext, useMemo } from 'react';
import { ServiceConnectionMetadataModel } from '../../../api/api';
import useApiServiceConnectionMetadata from '../../FlowDesigner/Hooks/useApiServiceConnectionMetadata';
import { ViewLoader } from '../../FlowDesigner/Components/ViewLoader';

export interface IServiceConnectionMetadataContext {
	serviceConnectionsMetadata: ServiceConnectionMetadataModel[];
	serviceConnectionByName: Map<string, ServiceConnectionMetadataModel>;
	isLoading: boolean;
}

class ServiceConnectionMetadataContextImpl implements IServiceConnectionMetadataContext {
	serviceConnectionsMetadata: ServiceConnectionMetadataModel[];
	serviceConnectionByName: Map<string, ServiceConnectionMetadataModel>;
	isLoading: boolean;

	constructor(serviceConnectionMetadata: ServiceConnectionMetadataModel[], isLoading: boolean = false) {
		this.serviceConnectionsMetadata = serviceConnectionMetadata;
		this.isLoading = isLoading;

		this.serviceConnectionByName = this.serviceConnectionsMetadata.reduce((acc, serviceConnection) => {
			acc.set(serviceConnection.name, serviceConnection);
			return acc;
		}, new Map<string, ServiceConnectionMetadataModel>());
	}
}

const emptyServiceConnectionMetadata: IServiceConnectionMetadataContext = {
	serviceConnectionsMetadata: [],
	serviceConnectionByName: new Map<string, ServiceConnectionMetadataModel>(),
	isLoading: true,
};

export const ServiceConnectionMetadataContext = createContext<IServiceConnectionMetadataContext>(emptyServiceConnectionMetadata);

export default function ServiceConnectionMetadataProvider({ children }: React.PropsWithChildren<{}>) {
	const [serviceConnectionMetadataContext, setServiceConnectionMetadataContext] =
		React.useState<IServiceConnectionMetadataContext>(emptyServiceConnectionMetadata);

	const { data: serviceConnectionMetadata, isLoading: serviceConnectionMetadataLoading } = useApiServiceConnectionMetadata();

	const isLoaded = useMemo(() => !serviceConnectionMetadataLoading && serviceConnectionMetadata, [serviceConnectionMetadataLoading, serviceConnectionMetadata]);

	React.useEffect(() => {
		if (serviceConnectionMetadata) {
			setServiceConnectionMetadataContext(new ServiceConnectionMetadataContextImpl(serviceConnectionMetadata, false));
		} else if (serviceConnectionMetadataLoading) {
			setServiceConnectionMetadataContext(new ServiceConnectionMetadataContextImpl([], true));
		}
	}, [serviceConnectionMetadata, serviceConnectionMetadataLoading]);

	return (
		<ServiceConnectionMetadataContext.Provider value={serviceConnectionMetadataContext}>
			{!isLoaded && <ViewLoader />}
			{isLoaded && children}
		</ServiceConnectionMetadataContext.Provider>
	);
}
